/**
 * @file stock-update-alert-modal.tsx
 * @module app/modals/stock-update-alert-modal
 * @description Renders the stock-update alert popup with warehouse, driver name,
 * and the custom audit log message for Missing / Damaged at Warehouse / Expired / Near Expiry updates.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.3.6
 * @version 2.1.2
 */

import { Button } from 'react-bootstrap';
import Modal from 'react-bootstrap/Modal';
import CrossSvg from '../../_admin/assets/media/close.png';

export interface StockUpdateAlertItem {
  id: string;
  warehouseName?: string;
  driverName?: string;
  message?: string;
}

interface StockUpdateAlertModalProps {
  show: boolean;
  alerts: StockUpdateAlertItem[];
  onClose: () => void;
}

const displayValue = (value?: string) => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : '—';
};

const StockUpdateAlertModal = ({
  show,
  alerts,
  onClose,
}: StockUpdateAlertModalProps) => {
  if (!alerts.length) {
    return null;
  }

  return (
    <Modal
      show={show}
      onHide={onClose}
      dialogClassName="modal-dialog-centered min-w-lg-700px"
      className="border-r10px"
      contentClassName="p-5"
    >
      <Modal.Header className="border-bottom-0 text-center pb-6 mx-auto">
        <div className="symbol symbol-md-40px symbol-35px close-inner-top cursor-pointer">
          <img
            width={40}
            height={40}
            src={CrossSvg}
            alt="close"
            onClick={onClose}
          />
        </div>
        <Modal.Title className="fs-30 fw-bolder mw-lg-400px">
          Stock update alert
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-0 border border-r10px">
        <div className="table-responsive">
          <table className="table table-rounded table-row-bordered align-middle gs-7 gy-6 mb-0">
            <thead>
              <tr className="fs-16 fw-bold text-dark border-bottom h-70px align-middle">
                <th>Warehouse</th>
                <th>Driver name</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {alerts.map((alert) => (
                <tr key={alert.id}>
                  <td className="fs-16 fw-500 text-break">
                    {displayValue(alert.warehouseName)}
                  </td>
                  <td className="fs-16 fw-500 text-break">
                    {displayValue(alert.driverName)}
                  </td>
                  <td className="fs-16 fw-500 text-break">
                    {displayValue(alert.message)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Modal.Body>
      <Modal.Footer className="justify-content-center pt-6 border-top-0">
        <Button variant="primary" className="uw-btn uw-btn-primary fs-16 fw-600 min-w-100px" onClick={onClose}>
          Got it
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default StockUpdateAlertModal;
